// Carry Roll HazardCheck — carry vs hazard yardage (10.3) + back-stop (emergent TODO-13).

import { useState } from 'react';
import { Check, X } from 'lucide-react';
import { ExpressionInput } from 'calc-controls';
import { useStore } from '../../store';
import { useResult, useRequest } from '../../store/selectors';
import { hazardCheck } from '@carry-roll/shared-logic';
import { sensitivity } from '@carry-roll/shared-logic';
import { distanceToM, mToYd } from '@carry-roll/shared-logic';
import { ConfidenceLevel } from '@carry-roll/shared-types';
import { confidence } from '@carry-roll/shared-logic';
import { HelpTooltip } from './HelpTooltip';

export function HazardCheck() {
  const result = useResult();
  const request = useRequest();
  const distanceUnit = useStore((s) => s.distanceUnit);
  const [hazardDist, setHazardDist] = useState(0);
  const [backStopDist, setBackStopDist] = useState(0);

  const hazardM = hazardDist > 0 ? distanceToM(hazardDist, distanceUnit) : null;
  const check = hazardM != null ? hazardCheck(result.carryM, hazardM) : null;

  // Back-stop: hazardCheck(carry+roll, backStopM). pass here means the ball is
  // PAST the back stop (carry+roll > backStopM). So STOP = !past.
  const backStopM = backStopDist > 0 ? distanceToM(backStopDist, distanceUnit) : null;
  const back = backStopM != null ? hazardCheck(result.carryM + result.rollM, backStopM) : null;
  const backStopped = back != null ? back.marginM <= 0 : null;

  // Remedy sub-panel (TODO-14): only on a SHORT front verdict.
  const remedies = check != null && !check.pass && check.marginM < 0
    ? buildRemedies(request, Math.abs(check.marginM))
    : [];

  // Confidence modulation (TODO-15): marginal warning when assumed + small margin.
  const conf = confidence(request);
  const marginal =
    conf === ConfidenceLevel.Assumed &&
    check != null &&
    Math.abs(check.marginM) < 5;

  const convMargin = (m: number) => Math.round(distanceUnit === 'yd' ? mToYd(Math.abs(m)) : Math.abs(m));

  return (
    <div data-testid="hazard-check">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs inline-flex items-center" style={{ color: 'var(--color-text-muted)' }}>
          Hazard at:
          <HelpTooltip label="Hazard check">Checks whether your carry clears the hazard distance. CLEAR is shown when carry exceeds it.</HelpTooltip>
        </span>
        <ExpressionInput
          inline
          value={hazardDist}
          onChange={setHazardDist}
          unit={distanceUnit}
          aria-label="Hazard distance"
          decimals={0}
          min={0}
        />
        {check && (
          <span
            className={`badge ${check.pass ? 'badge-success' : marginal ? 'badge-warn' : 'badge-danger'}`}
            role="status"
          >
            {check.pass ? (
              <>
                <Check size={12} aria-hidden="true" style={{ verticalAlign: 'middle' }} /> CLEAR
              </>
            ) : (
              <>
                <X size={12} aria-hidden="true" style={{ verticalAlign: 'middle' }} /> SHORT
              </>
            )}{' '}
            by {convMargin(check.marginM)} {distanceUnit}
          </span>
        )}
      </div>

      {back != null && backStopped != null && (
        <div data-testid="hazard-back-stop" className="flex items-center gap-2 mt-1 flex-wrap">
          <span className="text-xs inline-flex items-center" style={{ color: 'var(--color-text-muted)' }}>
            Back stop at:
            <HelpTooltip label="Back stop">Checks whether carry + roll stops before the back-stop distance. STOP is shown when the ball finishes short of it.</HelpTooltip>
          </span>
          <ExpressionInput
            inline
            value={backStopDist}
            onChange={setBackStopDist}
            unit={distanceUnit}
            aria-label="Back stop distance"
            decimals={0}
            min={0}
          />
          <span
            className={`badge ${backStopped ? 'badge-success' : 'badge-warn'}`}
            role="status"
          >
            {backStopped ? (
              <>
                <Check size={12} aria-hidden="true" style={{ verticalAlign: 'middle' }} /> STOP
              </>
            ) : (
              <>
                <X size={12} aria-hidden="true" style={{ verticalAlign: 'middle' }} /> RUN-THROUGH
              </>
            )}{' '}
            by {convMargin(back.marginM)} {distanceUnit}
          </span>
        </div>
      )}

      {marginal && (
        <div className="text-xs mt-1" style={{ color: 'var(--color-warn)' }}>
          ⚠ Result is uncertain — most inputs are still set to defaults, so the margin may be off.
        </div>
      )}

      {remedies.length > 0 && (
        <div data-testid="hazard-remedy" className="mt-1">
          <div className="text-xs mb-1" style={{ color: 'var(--color-text-muted)' }}>
            Close the gap (+{Math.round(distanceUnit === 'yd' ? mToYd(Math.abs(check!.marginM)) : Math.abs(check!.marginM))} {distanceUnit} short):
          </div>
          {remedies.map((r, i) => (
            <div key={i} className="text-xs">
              {r}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function buildRemedies(request: ReturnType<typeof useRequest>, shortfallM: number): string[] {
  const sens = sensitivity(request);
  const byParam = new Map(sens.map((s) => [s.parameter, s]));
  const out: string[] = [];

  const launchSens = byParam.get('Launch angle');
  if (launchSens && Math.abs(launchSens.dCarryPerUnit) > 1e-6) {
    const delta = shortfallM / Math.abs(launchSens.dCarryPerUnit);
    out.push(`+${delta.toFixed(1)}° launch`);
  }

  const bsSens = byParam.get('Ball speed');
  if (bsSens && Math.abs(bsSens.dCarryPerUnit) > 1e-6) {
    const delta = shortfallM / Math.abs(bsSens.dCarryPerUnit);
    out.push(`+${delta.toFixed(1)} m/s ball speed`);
  }

  // Next club from the bag whose carry exceeds the current carry + shortfall.
  const bag = useStore.getState().bag;
  const currentCarry = sens.length > 0 ? shortfallM : 0; // not used; reference only
  void currentCarry;
  const currentResultCarry = useStore.getState().result.carryM;
  const nextClub = bag.find((b) => b.carryM >= currentResultCarry + shortfallM);
  if (nextClub) {
    out.push(`Club up → ${nextClub.clubName} (${nextClub.carryM.toFixed(0)} m carry)`);
  }

  return out.slice(0, 3);
}
